"use client";
import { GlassCard } from "@/src/components/GlassCard";
import { Button } from "@/src/components/ui/button";
import { ImageWithFallback } from "@/src/components/figma/ImageWithFallback";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Facebook,
  Linkedin,
  Mail,
  Share2,
  Tag,
  Twitter,
} from "lucide-react";
import { motion } from "motion/react";
import Link from "next/link";
import React from "react";
import { useTranslation } from "react-i18next";

export const BlogPostPage: React.FC = () => {
  const { t } = useTranslation("common");
  // Mock blog post data - in real app, fetch based on slug
  const post = {
    title: "2026 Packaging Trends: What to Expect",
    excerpt:
      "Explore the latest trends shaping the packaging industry, from sustainable materials to smart packaging technology.",
    category: "Industry Trends",
    date: "February 15, 2026",
    readTime: "5 min read",
    author: {
      name: "Sarah Johnson",
      role: "Industry Expert",
      avatar:
        "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop",
    },
    image:
      "https://images.unsplash.com/photo-1631010231888-777b6285ef84?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjdXN0b20lMjByZXRhaWwlMjBwcm9kdWN0JTIwYm94ZXN8ZW58MXx8fHwxNzcxNjA3OTQ2fDA&ixlib=rb-4.1.0&q=80&w=1080",
    tags: ["Sustainability", "Innovation", "Design", "E-commerce"],
    content: `
      <h2>The Future of Packaging is Here</h2>
      <p>The packaging industry is experiencing a remarkable transformation in 2026. As consumer expectations evolve and environmental concerns take center stage, businesses must adapt to stay competitive. This comprehensive guide explores the key trends reshaping how we think about packaging.</p>

      <h3>1. Sustainable Materials Lead the Way</h3>
      <p>Eco-friendly packaging is no longer a nice-to-have—it's essential. Consumers actively seek brands that demonstrate environmental responsibility. We're seeing unprecedented growth in:</p>
      <ul>
        <li><strong>Biodegradable materials</strong> - Plant-based plastics and mushroom packaging</li>
        <li><strong>Recycled content</strong> - Post-consumer recycled materials becoming standard</li>
        <li><strong>Compostable solutions</strong> - Packaging that returns to the earth safely</li>
        <li><strong>Ocean-friendly options</strong> - Materials that break down in marine environments</li>
      </ul>

      <h3>2. Smart Packaging Technology</h3>
      <p>The integration of technology into packaging is creating new opportunities for brands to engage with customers. QR codes, NFC chips, and AR experiences are transforming boxes into interactive brand touchpoints.</p>
      <p>Smart packaging enables:</p>
      <ul>
        <li>Product authentication and anti-counterfeiting measures</li>
        <li>Real-time supply chain tracking</li>
        <li>Enhanced customer engagement through AR experiences</li>
        <li>Freshness indicators for food products</li>
      </ul>

      <h3>3. Minimalist Design Aesthetics</h3>
      <p>Less is more in 2026. Clean, minimalist packaging designs are dominating shelves as brands embrace simplicity. This trend reflects consumer desire for transparency and authenticity.</p>
      <p>Key characteristics include:</p>
      <ul>
        <li>Minimal color palettes with bold typography</li>
        <li>Emphasis on negative space</li>
        <li>Clear, honest communication</li>
        <li>Sustainable material showcasing</li>
      </ul>

      <h3>4. Personalization at Scale</h3>
      <p>Digital printing technology now makes it economically viable to create personalized packaging for individual customers. This trend is particularly strong in e-commerce, where the unboxing experience can be tailored to enhance brand loyalty.</p>

      <h3>5. Circular Economy Integration</h3>
      <p>Forward-thinking brands are designing packaging with its end-of-life in mind. Circular packaging systems that can be returned, refilled, or repurposed are gaining traction.</p>

      <h2>Implementing These Trends</h2>
      <p>At Walnut Packaging Factory, we're at the forefront of these innovations. Our die-cut design studio allows you to experiment with sustainable materials while our 3D preview technology lets you visualize the final product before production.</p>
      <p>Whether you're looking to implement smart packaging features or create a stunning minimalist design, our team is here to help you navigate these exciting trends.</p>

      <h3>Getting Started</h3>
      <p>Ready to embrace the future of packaging? Here's how to begin:</p>
      <ol>
        <li>Audit your current packaging strategy</li>
        <li>Identify which trends align with your brand values</li>
        <li>Consult with packaging experts (that's us!)</li>
        <li>Test new designs with your target audience</li>
        <li>Implement and iterate based on feedback</li>
      </ol>

      <h2>Conclusion</h2>
      <p>The packaging landscape of 2026 offers incredible opportunities for brands willing to innovate. By embracing sustainability, technology, and design excellence, you can create packaging that not only protects your products but also tells your brand story and resonates with conscious consumers.</p>
    `,
  };

  const relatedPosts = [
    {
      title: "The Ultimate Guide to Sustainable Packaging",
      category: "Sustainability",
      image:
        "https://images.unsplash.com/photo-1755606396356-bdd7cd95df75?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzdXN0YWluYWJsZSUyMGVjbyUyMHBhY2thZ2luZyUyMG1hdGVyaWFsc3xlbnwxfHx8fDE3NzE2MDc5NDV8MA&ixlib=rb-4.1.0&q=80&w=1080",
      slug: "sustainable-packaging-guide",
    },
    {
      title: "How Custom Packaging Boosts E-commerce Sales",
      category: "Case Studies",
      image:
        "https://images.unsplash.com/photo-1523474253046-8cd2748b5fd2?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxlY29tbWVyY2UlMjBwYWNrYWdpbmclMjB1bmJveGluZ3xlbnwxfHx8fDE3NzE2MDc5NDZ8MA&ixlib=rb-4.1.0&q=80&w=1080",
      slug: "ecommerce-packaging-sales",
    },
  ];

  return (
    <div className="min-h-screen pt-32 pb-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-8"
        >
          <Link
            href="/blog"
            className="inline-flex items-center gap-2 text-walnut-700 dark:text-walnut-300 hover:text-walnut-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">{t("marketingPages.blogPost.back")}</span>
          </Link>
        </motion.div>

        {/* Article Header */}
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          {/* Category Badge */}
          <div className="mb-4">
            <span className="inline-block px-4 py-2 rounded-full backdrop-blur-xl bg-walnut-600/80 text-white text-sm font-medium">
              {post.category}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-walnut-900 dark:text-white mb-6">
            {post.title}
          </h1>

          {/* Meta Information */}
          <div className="flex flex-wrap items-center gap-4 mb-8 text-walnut-700 dark:text-walnut-300">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span className="text-sm">{post.date}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span className="text-sm">{post.readTime}</span>
            </div>
          </div>

          {/* Author Info */}
          <div className="flex items-center gap-4 mb-8 pb-8 border-b border-white/20 dark:border-white/10">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-walnut-400 to-walnut-600 overflow-hidden">
              <ImageWithFallback
                src={post.author.avatar}
                alt={post.author.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <div className="font-semibold text-walnut-900 dark:text-white">
                {post.author.name}
              </div>
              <div className="text-sm text-walnut-600 dark:text-walnut-400">
                {post.author.role}
              </div>
            </div>
          </div>

          {/* Featured Image */}
          <div className="mb-12 rounded-[33px] overflow-hidden">
            <ImageWithFallback
              src={post.image}
              alt={post.title}
              className="w-full h-[400px] object-cover"
            />
          </div>

          {/* Article Content */}
          <GlassCard className="p-8 md:p-12 mb-12">
            <div
              className="prose prose-lg dark:prose-invert max-w-none
                prose-headings:text-walnut-900 dark:prose-headings:text-white
                prose-h2:text-3xl prose-h2:font-bold prose-h2:mb-4 prose-h2:mt-8
                prose-h3:text-2xl prose-h3:font-semibold prose-h3:mb-3 prose-h3:mt-6
                prose-p:text-walnut-700 dark:prose-p:text-walnut-300 prose-p:mb-4 prose-p:leading-relaxed
                prose-li:text-walnut-700 dark:prose-li:text-walnut-300
                prose-strong:text-walnut-900 dark:prose-strong:text-white
                prose-ul:my-4 prose-ol:my-4"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </GlassCard>

          {/* Tags */}
          <div className="mb-12">
            <div className="flex flex-wrap items-center gap-2">
              <Tag className="w-5 h-5 text-walnut-600 dark:text-walnut-400" />
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-4 py-2 rounded-full backdrop-blur-xl bg-white/20 dark:bg-black/20 border border-white/30 dark:border-white/10 text-sm text-walnut-700 dark:text-walnut-300"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Share Section */}
          <GlassCard className="p-6 mb-12">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Share2 className="w-5 h-5 text-walnut-600 dark:text-walnut-400" />
                <span className="font-semibold text-walnut-900 dark:text-white">
                  {t("marketingPages.blogPost.shareArticle")}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Button variant="glass" size="icon" className="rounded-full">
                  <Facebook className="w-5 h-5 text-walnut-700 dark:text-walnut-300" />
                </Button>
                <Button variant="glass" size="icon" className="rounded-full">
                  <Twitter className="w-5 h-5 text-walnut-700 dark:text-walnut-300" />
                </Button>
                <Button variant="glass" size="icon" className="rounded-full">
                  <Linkedin className="w-5 h-5 text-walnut-700 dark:text-walnut-300" />
                </Button>
                <Button variant="glass" size="icon" className="rounded-full">
                  <Mail className="w-5 h-5 text-walnut-700 dark:text-walnut-300" />
                </Button>
              </div>
            </div>
          </GlassCard>

          {/* CTA Section */}
          <GlassCard className="p-8 text-center mb-12">
            <h3 className="text-2xl font-bold text-walnut-900 dark:text-white mb-3">
              {t("marketingPages.blogPost.ctaTitle")}
            </h3>
            <p className="text-walnut-700 dark:text-walnut-300 mb-6">
              {t("marketingPages.blogPost.ctaSubtitle")}
            </p>
            <Link
              href="/quote"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-[33px] bg-gradient-to-r from-walnut-600 to-walnut-700 dark:from-walnut-500 dark:to-walnut-600 text-white font-semibold hover:shadow-lg hover:shadow-walnut-500/30 transition-all duration-300 hover:scale-105"
            >
              {t("marketingPages.blogPost.ctaButton")}
            </Link>
          </GlassCard>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <div>
              <h3 className="text-2xl font-bold text-walnut-900 dark:text-white mb-6">
                {t("marketingPages.blogPost.relatedArticles")}
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                {relatedPosts.map((relatedPost, index) => (
                  <motion.div
                    key={relatedPost.slug}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * index }}
                  >
                    <Link href={`/blog/${relatedPost.slug}`}>
                      <GlassCard className="overflow-hidden hover:scale-[1.02] transition-all duration-300 cursor-pointer">
                        <div className="relative h-48">
                          <ImageWithFallback
                            src={relatedPost.image}
                            alt={relatedPost.title}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute top-4 left-4">
                            <span className="px-3 py-1 rounded-full backdrop-blur-xl bg-walnut-600/80 text-white text-sm">
                              {relatedPost.category}
                            </span>
                          </div>
                        </div>
                        <div className="p-6">
                          <h4 className="text-xl font-semibold text-walnut-900 dark:text-white">
                            {relatedPost.title}
                          </h4>
                        </div>
                      </GlassCard>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </motion.article>
      </div>
    </div>
  );
};
